'use client';

import Image from '@/components/Image';
import ProductCard from '@/components/Product/ProductCard';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { setSession } from '@/utils/tools';
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { LensPartData } from '../types';

interface LensPartProps {
  lensPartData: LensPartData;
  onProductClick?: (product: any, index: number) => void;
  onProductExp?: (product: any, index: number) => void;
}

export function LensPart({ lensPartData, onProductClick, onProductExp }: LensPartProps) {
  const [productList, setProductList] = useState<any[]>(lensPartData.lists || []);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const swiperRef = useRef<any>(null);
  const { navigate } = useAppNavigation();

  // 当传入的商品列表更新时，更新本地状态
  useEffect(() => {
    setProductList(lensPartData.lists || []);
  }, [lensPartData.lists]);

  // 确保产品列表加载后状态正确更新
  useEffect(() => {
    if (productList.length > 0 && swiperRef.current) {
      setTimeout(() => {
        if (swiperRef.current) {
          setIsBeginning(swiperRef.current.isBeginning);
          setIsEnd(swiperRef.current.isEnd);
        }
      }, 200);
    }
  }, [productList]);

  // 处理按钮点击
  const handleBtnClick = () => {
    if (lensPartData.group_id) {
      setSession('zl_goodsListEnterPageScene', '活动专题页');
      navigate(`/goods-list/${lensPartData.group_id}`);
    }
  };

  // 处理产品点击
  const handleProductClick = (index: number, product: any) => {
    onProductClick?.(product, index);
  };

  // 处理产品曝光
  const handleProductExp = (index: number, product: any) => {
    onProductExp?.(product, index);
  };

  // 切换到上一张
  const handlePrevSlide = () => {
    if (!isBeginning && swiperRef.current) {
      swiperRef.current.slidePrev();
      // 延迟更新状态确保同步
      setTimeout(() => {
        if (swiperRef.current) {
          setIsBeginning(swiperRef.current.isBeginning);
          setIsEnd(swiperRef.current.isEnd);
        }
      }, 100);
    }
  };

  // 切换到下一张
  const handleNextSlide = () => {
    if (!isEnd && swiperRef.current) {
      swiperRef.current.slideNext();
      // 延迟更新状态确保同步
      setTimeout(() => {
        if (swiperRef.current) {
          setIsBeginning(swiperRef.current.isBeginning);
          setIsEnd(swiperRef.current.isEnd);
        }
      }, 100);
    }
  };

  // PC端产品列表渲染
  const renderProductListPC = () => (
    <div className="swiper-nav-container relative overflow-visible">
      <Swiper
        slidesPerView="auto"
        slidesPerGroup={1}
        modules={[Navigation]}
        slidesOffsetAfter={48}
        slidesOffsetBefore={48}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
        onInit={(swiper) => {
          setIsBeginning(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
        observer
        observeParents
        observeSlideChildren
        breakpoints={{
          1536: {
            allowTouchMove: false,
          },
        }}
      >
        {productList.map((product, index) => (
          <SwiperSlide
            key={product.id || index}
            className="!w-[23%] mr-[16px] last:!mr-0 !min-w-[356px] 2xl:!min-w-[auto]"
          >
            <ProductCard
              product={product}
              index={index}
              showTryOn={false}
              handelClick={() => handleProductClick(index, product)}
              onInView={() => handleProductExp(index, product)}
              className={'h-full bg-none'}
              showCorrelation
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* 自定义切换按钮 - 只在2xl及以上屏幕显示 */}
      <div className="absolute right-[48px] top-[-60px] flex 2xl:hidden gap-[15px] z-10">
        {/* 左切换按钮 */}
        <button
          onClick={handlePrevSlide}
          disabled={isBeginning}
          className={`w-[34px] h-[34px] rounded-full flex items-center justify-center ${
            isBeginning
              ? 'border border-[#8D8D92] cursor-not-allowed'
              : 'hover:scale-110 cursor-pointer border border-[#282828]'
          }`}
        >
          <LeftArrowOutlined
            className={`text-[20px] ${isBeginning ? 'text-[#8D8D92]' : 'text-[#282828]'}`}
          />
        </button>

        {/* 右切换按钮 */}
        <button
          onClick={handleNextSlide}
          disabled={isEnd}
          className={`w-[34px] h-[34px] rounded-full flex items-center justify-center ${
            isEnd
              ? 'border border-[#8D8D92] cursor-not-allowed'
              : 'hover:scale-110 cursor-pointer border border-[#282828]'
          }`}
        >
          <RightArrowOutlined
            className={`text-[20px] ${isEnd ? 'text-[#8D8D92]' : 'text-[#282828]'}`}
          />
        </button>
      </div>
    </div>
  );

  // 移动端产品列表渲染
  const renderProductListMobile = () => (
    <div>
      <Swiper
        spaceBetween={5}
        slidesPerView={2.2}
        slidesOffsetBefore={14}
        slidesOffsetAfter={14}
        observer
        observeParents
        observeSlideChildren
      >
        {productList.map((product, index) => (
          <SwiperSlide key={product.id || index}>
            <ProductCard
              product={product}
              index={index}
              showTryOn={false}
              handelClick={() => handleProductClick(index, product)}
              onInView={() => handleProductExp(index, product)}
              className={'bg-none pb-[2px]'}
              imageClassName={'h-[176px] rounded-[12px]'}
              showCorrelation
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );

  return (
    <div className="w-full pb-[60px] lg:pb-[120px] box-border">
      {/* 顶部内容区域 */}
      <div className="px-[14px] lg:px-[48px] box-border">
        {/* 标题 */}
        {lensPartData.title && (
          <h1 className="text-[24px] leading-[29px] lg:text-[56px] lg:leading-[67px] text-center font-bb mt-[44px] mb-[22px] lg:mt-[88px] lg:mb-[40px]">
            {lensPartData.title}
          </h1>
        )}

        {/* PC端布局 */}
        <WebOnly>
          <div
            className={`flex items-center justify-between gap-[4.1666666667vw] ${lensPartData.img_position_change ? 'flex-row-reverse' : ''}`}
          >
            {/* 主图片 */}
            {lensPartData.main_img && (
              <Image
                src={lensPartData.main_img}
                alt={lensPartData.level_two_title || ''}
                className="w-[49.0625vw] h-auto"
                width={942}
                height={600}
              />
            )}

            {/* 右侧内容 */}
            <div className="flex-1">
              {/* 二级标题 */}
              {lensPartData.level_two_title && (
                <h3 className="text-[2.0833333333vw] leading-[2.5vw] font-bb mb-[0.8333333333vw]">
                  {lensPartData.level_two_title}
                </h3>
              )}

              {/* 描述 */}
              {lensPartData.level_two_description && (
                <p className="text-[0.8333333333vw] mb-[1.25vw]">
                  {lensPartData.level_two_description}
                </p>
              )}

              {/* 特性列表 */}
              {lensPartData.feature_arr && lensPartData.feature_arr.length > 0 && (
                <ul className="grid grid-cols-2 gap-[1.25vw] mb-[2.0833333333vw] mr-[9.7916666667vw]">
                  {lensPartData.feature_arr.map((item, index) => (
                    <li key={index} className="flex items-center">
                      <Image
                        src={item.image}
                        alt={item.title}
                        className="w-[3.2604166667vw] h-auto"
                        width={64}
                        height={64}
                      />
                      <p className="flex-1 ml-[0.8333333333vw] text-[0.8333333333vw]">
                        {item.word}
                      </p>
                    </li>
                  ))}
                </ul>
              )}

              {/* 颜色标题 */}
              {lensPartData.color_title && (
                <p className="text-[1.0416666667vw] font-bb mb-[1.25vw]">
                  {lensPartData.color_title}
                </p>
              )}

              {/* 颜色选项 */}
              {lensPartData.color_arr && lensPartData.color_arr.length > 0 && (
                <ul className="flex flex-wrap gap-[0.8333333333vw] mb-[1.1979vw]">
                  {lensPartData.color_arr.map((color, index) => (
                    <li key={index}>
                      <Image
                        src={color.image}
                        alt={`${lensPartData.color_title} ${index}`}
                        className="w-[3.90625vw] h-auto"
                        width={75}
                        height={75}
                      />
                      <p className="w-[3.90625vw] text-[12px] xl:text-[14px] leading-[14px] xl:leading-[17px] text-center mt-[4px] xl:mt-[8px]">
                        {color.title}
                      </p>
                    </li>
                  ))}
                </ul>
              )}

              {/* 按钮 */}
              {lensPartData.btn_word && (
                <button
                  onClick={handleBtnClick}
                  className="w-[18.8020833333vw] h-[2.9166666667vw] rounded-[25px] bg-primary-black text-white text-[0.8333333333vw] font-bb text-center leading-[2.9166666667vw] cursor-pointer hover:opacity-90 transition-opacity"
                >
                  {lensPartData.btn_word}
                </button>
              )}
            </div>
          </div>
        </WebOnly>

        {/* 移动端布局 */}
        <WapOnly>
          {/* 主图片 */}
          {lensPartData.main_img && (
            <Image
              src={lensPartData.main_img}
              alt={lensPartData.level_two_title || ''}
              className="w-full h-auto"
              width={375}
              height={250}
            />
          )}

          {/* 二级标题 */}
          {lensPartData.level_two_title && (
            <h3 className="text-[20px] leading-[24px] text-center mt-[24px] mb-[12px] font-bb">
              {lensPartData.level_two_title}
            </h3>
          )}

          {/* 描述 */}
          {lensPartData.level_two_description && (
            <p className="text-[13px] leading-[20px] mb-[20px]">
              {lensPartData.level_two_description}
            </p>
          )}

          {/* 特性列表 */}
          {lensPartData.feature_arr && lensPartData.feature_arr.length > 0 && (
            <ul className="grid grid-cols-2 gap-[15px] mb-[25px]">
              {lensPartData.feature_arr.map((item, index) => (
                <li key={index} className="flex items-center">
                  <Image
                    src={item.image}
                    alt={item.title}
                    className="w-[40px] h-auto"
                    width={40}
                    height={40}
                  />
                  <p className="ml-[10px] text-[13px] leading-[16px] flex-1">{item.word}</p>
                </li>
              ))}
            </ul>
          )}

          {/* 颜色标题 */}
          {lensPartData.color_title && (
            <p className="text-[16px] leading-[20px] font-bb mb-[20px] text-center">
              {lensPartData.color_title}
            </p>
          )}

          {/* 颜色选项 */}
          {lensPartData.color_arr && lensPartData.color_arr.length > 0 && (
            <ul className="flex flex-wrap justify-center gap-[8px] gap-y-[12px] mb-[24px]">
              {lensPartData.color_arr.map((color, index) => (
                <li key={index}>
                  <Image
                    src={color.image}
                    alt={`${lensPartData.color_title} ${index}`}
                    className="w-[60px] h-auto"
                    width={60}
                    height={60}
                  />
                  <p className="w-[60px] text-[12px] leading-[14px] text-center mt-[6px]">
                    {color.title}
                  </p>
                </li>
              ))}
            </ul>
          )}

          {/* 按钮 */}
          {lensPartData.btn_word && (
            <button
              onClick={handleBtnClick}
              className="w-[240px] h-[36px] rounded-[25px] bg-primary-black text-white text-[14px] font-bb text-center leading-[36px] cursor-pointer mx-auto block"
            >
              {lensPartData.btn_word}
            </button>
          )}
        </WapOnly>
      </div>

      {/* 产品列表区域 */}
      {lensPartData.group_title && (
        <div className="mt-[40px] lg:mt-[88px]">
          <p className="text-center lg:text-left text-[24px] leading-[24px] 2xl:text-[40px] 2xl:leading-[48px] font-bb mb-[16px] lg:mb-[32px] px-[14px] lg:px-[48px]">
            {lensPartData.group_title}
          </p>

          {productList.length > 0 ? (
            <>
              <WebOnly>{renderProductListPC()}</WebOnly>
              <WapOnly>{renderProductListMobile()}</WapOnly>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
